import React, { useEffect, useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import useProcedureStore from '../stores/procedureStore';
import useDepartmentStore from '../stores/departmentStore';
import ProcedureFormRow from '../components/procedures/ProcedureFormRow';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import AdminPageHeader from '../components/common/AdminPageHeader';
import { ConfirmationModal } from '../components/common';
import {
  PROCEDURE_CATEGORIES,
  excludeLabTests,
  PROCEDURE_CATEGORY_SHORT_LABELS,
  PROCEDURE_CATEGORY_LABELS,
  PROCEDURE_FLAGS,
  PROCEDURE_FLAG_COLORS,
  categoryFromDeptName,
} from '../constants/procedures';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

const BLANK_NEW = {
  name: '',
  category: '',
  default_cost: '',
  gst_rate: 0,
  specialty: '',
  ada_code: '',
  is_tooth_specific: false,
  nurse_allowed: false,
  receptionist_allowed: false,
  skip_payment: false,
  one_time_only: false,
  one_time_only_reason: '',
  typical_duration_minutes: null,
  notes: '',
};

function validate(row, isEdit = false) {
  const errors = {};
  if (!row.name || !row.name.trim()) errors.name = 'Required';
  if (!isEdit && !row.category) errors.category = 'Required';
  if (row.default_cost === '' || row.default_cost === null || row.default_cost === undefined) {
    errors.default_cost = 'Required';
  } else if (Number.isNaN(Number(row.default_cost)) || Number(row.default_cost) < 0) {
    errors.default_cost = 'Must be 0 or more';
  }
  if (row.is_tooth_specific && row.category !== 'dental') {
    errors.is_tooth_specific = 'Tooth-specific requires dental';
  }
  return errors;
}

function FlagChip({ flag }) {
  const colors = PROCEDURE_FLAG_COLORS[flag.color] || {};
  return (
    <span
      className="pcp-chip"
      style={{ background: colors.bg, color: colors.text, borderColor: colors.border }}
    >
      {flag.label}
    </span>
  );
}

export default function ProcedureCatalogPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  // Check permission before hook-dependent computation. The early return
  // itself MUST come after all hook calls (Rules of Hooks) — see end of
  // component body.

  const {
    procedures,
    selectedCategory,
    includeInactive,
    searchQuery,
    isLoading,
    error,
    fetchProcedures,
    createProcedure,
    updateProcedure,
    deactivateProcedure,
    setCategory,
    setIncludeInactive,
    setSearch,
    clearError,
    setProcedureDepartments,
  } = useProcedureStore();

  // A4/S2 — org departments for the "Departments (optional, multi)" tag field.
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  // Only ACTIVE departments are offered (a soft-deleted dept drops out; a tag to
  // a dead dept simply won't render a checkbox). AC3.
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);

  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({ ...BLANK_NEW });
  const [newErrors, setNewErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A4/S2 — dept picker selection for the CREATE form (bug 2). Saved via C2
  // right after the create succeeds, as part of the same "Create" click.
  const [newDeptIds, setNewDeptIds] = useState([]);

  const [editingKey, setEditingKey] = useState(null); // `${category}:${procedure_id}`
  const [editRow, setEditRow] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  // A4/S2 — dept-tag editor state, seeded from the row's current department_ids
  // on edit-open. Saved as part of the SAME "Save Changes" click as the rest of
  // the form (C2 PUT alongside the PATCH) — no standalone save action.
  const [editDeptIds, setEditDeptIds] = useState([]);

  const [confirmDialog, setConfirmDialog] = useState(null);
  const [banner, setBanner] = useState(null); // { kind: 'ok'|'err', msg: string }
  // B5 (bug 3) — MEMBERSHIP list dept-filter (client-side over already-loaded
  // procedures — no new fetch on change). Empty = all; a dept = only rows tagged
  // to it; "Org-wide" = only untagged.
  const [deptFilter, setDeptFilter] = useState('');

  // ── Initial load ──────────────────────────────────────────────
  useEffect(() => {
    fetchProcedures();
    fetchDepartments();
  }, [fetchProcedures, fetchDepartments]);

  // ── Client-side filters: category + department (membership) + search ──
  const visible = useMemo(() => {
    let list = excludeLabTests(procedures);
    if (selectedCategory && selectedCategory !== 'all') {
      list = list.filter((p) => p.category === selectedCategory);
    }
    list = filterByDeptMembership(list, deptFilter);
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.procedure_id || '').toLowerCase().includes(q) ||
          (p.specialty || '').toLowerCase().includes(q) ||
          (p.ada_code || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [procedures, selectedCategory, searchQuery, deptFilter]);

  // ── Per-category counts (for tab badges) ──────────────────────
  const counts = useMemo(() => {
    const procs = excludeLabTests(procedures);
    const byCat = { all: procs.length };
    for (const c of PROCEDURE_CATEGORIES) byCat[c.value] = 0;
    for (const p of procs) {
      if (byCat[p.category] !== undefined) byCat[p.category] += 1;
    }
    return byCat;
  }, [procedures]);

  // ── Add handlers ──────────────────────────────────────────────
  const openAdd = () => {
    setNewRow({ ...BLANK_NEW });
    setNewErrors({});
    setNewDeptIds([]);
    setBanner(null); // bug 4 — a stale toast from a prior context must not leak in here
    setShowAdd(true);
  };
  const cancelAdd = () => {
    setShowAdd(false);
    setNewErrors({});
    setNewDeptIds([]);
    setBanner(null); // bug 4
  };
  const changeNew = (field, value) => {
    setNewRow((r) => {
      const next = { ...r, [field]: value };
      if (field === 'category' && value !== 'dental') {
        next.is_tooth_specific = false;
        next.ada_code = '';
      }
      return next;
    });
  };
  const toggleNewDept = (deptId) => {
    setNewDeptIds((prev) => {
      const isAdding = !prev.includes(deptId);
      const next = isAdding ? [...prev, deptId] : prev.filter((d) => d !== deptId);
      // A4 smart-default (create form only): auto-fill Clinical type from a
      // clean dept→category match. Guards — only when: (1) selecting (not
      // deselecting) a department, (2) exactly one department ends up
      // selected (multi-select stays ambiguous, left to the user), and
      // (3) Clinical type is still empty (never overrides a user-set value).
      // Derived synchronously from already-loaded activeDepts — no fetch.
      if (isAdding && next.length === 1) {
        const matched = categoryFromDeptName(deptNameById[deptId]);
        if (matched) {
          setNewRow((r) => (r.category ? r : { ...r, category: matched }));
        }
      }
      return next;
    });
  };
  const submitNew = async () => {
    const errs = validate(newRow, false);
    setNewErrors(errs);
    if (Object.keys(errs).length) return;
    setIsSubmitting(true);
    const body = {
      ...newRow,
      default_cost: Number(newRow.default_cost),
      gst_rate: Number(newRow.gst_rate) || 0,
    };
    // Drop empty optional strings
    for (const k of ['specialty', 'ada_code', 'notes', 'one_time_only_reason']) {
      if (body[k] === '') delete body[k];
    }
    if (body.typical_duration_minutes === null) delete body.typical_duration_minutes;
    // `recurring` was removed from the backend catalog (extra=forbid → 422 if sent).
    delete body.recurring;
    // Create request schema is extra=forbid and has no owner_scope/department_ids
    // field — departments are tagged via the separate C2 call below, never sent here.
    const result = await createProcedure(body);
    setIsSubmitting(false);
    if (!result.success) {
      setBanner({ kind: 'err', msg: result.error || 'Create failed' });
      return;
    }
    setShowAdd(false);
    setNewRow({ ...BLANK_NEW });
    // Bug 2/3 — tag departments as part of the SAME "Create" action, using the
    // server-assigned procedure_id from the create response.
    const newProcedureId = result.data?.procedure_id;
    if (newDeptIds.length) {
      if (!newProcedureId) {
        setBanner({ kind: 'err', msg: 'Procedure created, but departments could not be saved — edit it to set departments.' });
        setNewDeptIds([]);
        return;
      }
      const deptResult = await setProcedureDepartments(body.category, newProcedureId, newDeptIds);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `Procedure created, but departments were not saved: ${deptResult.error || 'unknown error'} — edit it to set departments.` });
        setNewDeptIds([]);
        return;
      }
    }
    setNewDeptIds([]);
    setBanner({ kind: 'ok', msg: 'Procedure created' });
  };

  // ── Edit handlers ─────────────────────────────────────────────
  const startEdit = (row) => {
    setEditingKey(`${row.category}:${row.procedure_id}`);
    setEditRow({ ...row });
    setEditErrors({});
    // Seed the dept-tag editor from the row's current tags (active edges).
    setEditDeptIds(Array.isArray(row.department_ids) ? [...row.department_ids] : []);
    setBanner(null); // bug 4 — switching rows must not carry a stale toast
  };
  const cancelEdit = () => {
    setEditingKey(null);
    setEditRow(null);
    setEditErrors({});
    setEditDeptIds([]);
    setBanner(null); // bug 4
  };

  // A4/S2 — toggle a department in the edit selection (multi). Empty selection
  // = org-wide (implicit); ≥1 = DEPARTMENT scope, reflected after save.
  const toggleEditDept = (deptId) => {
    setEditDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]
    );
  };
  const changeEdit = (field, value) => {
    setEditRow((r) => ({ ...r, [field]: value }));
  };
  const submitEdit = async () => {
    if (!editRow) return;
    const errs = validate(editRow, true);
    setEditErrors(errs);
    if (Object.keys(errs).length) return;
    setIsUpdating(true);
    const body = { ...editRow };
    // PATCH only allowed fields — strip read-only / SK / enrichment fields.
    // `owner_scope` + `department_ids` arrive on the row via the C3 read
    // enrichment (list/get response) but are NOT part of
    // UpdateClinicProcedureRequest (extra=forbid) — sending them 422s
    // (bug 1). Departments are saved via the separate C2 call below instead.
    delete body.procedure_id;
    delete body.organization_id;
    delete body.category;
    delete body.created_at;
    delete body.updated_at;
    delete body.owner_scope;
    delete body.department_ids;
    // `recurring` was removed from the backend catalog (extra=forbid → 422). Old
    // rows still carry it, so the {...editRow} spread re-sends the stale value —
    // strip it explicitly.
    delete body.recurring;
    if (body.one_time_only_reason === '') delete body.one_time_only_reason;
    // Normalize
    body.default_cost = Number(body.default_cost);
    body.gst_rate = Number(body.gst_rate) || 0;
    const result = await updateProcedure(editRow.category, editRow.procedure_id, body);
    if (!result.success) {
      setIsUpdating(false);
      setBanner({ kind: 'err', msg: result.error || 'Update failed' });
      return;
    }
    // Bug 3 — departments save as part of the SAME "Save Changes" click, not a
    // separate button/action.
    const deptResult = await setProcedureDepartments(editRow.category, editRow.procedure_id, editDeptIds);
    setIsUpdating(false);
    if (!deptResult.success) {
      // Partial failure: the procedure fields saved, departments did not.
      // Keep the edit form open (don't claim full success) so the user can retry.
      setBanner({ kind: 'err', msg: `Procedure updated, but departments were not saved: ${deptResult.error || 'unknown error'}` });
      return;
    }
    cancelEdit();
    setBanner({ kind: 'ok', msg: 'Procedure updated' });
  };

  // ── Deactivate ─────────────────────────────────────────────────
  const askDeactivate = (row) => {
    setConfirmDialog({
      title: 'Deactivate procedure?',
      message: `"${row.name}" will be hidden from new treatment plans. Historical plans keep working. You can re-activate later by editing and toggling Active.`,
      ctaLabel: 'Deactivate',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog(null);
        const result = await deactivateProcedure(row.category, row.procedure_id);
        setBanner(
          result.success
            ? { kind: 'ok', msg: 'Procedure deactivated' }
            : { kind: 'err', msg: result.error || 'Deactivate failed' }
        );
      },
    });
  };

  // ── UI ─────────────────────────────────────────────────────────
  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const tabs = [{ value: 'all', label: `All` }, ...PROCEDURE_CATEGORIES.map((c) => ({
    value: c.value,
    label: PROCEDURE_CATEGORY_SHORT_LABELS[c.value] || c.label,
  }))];

  // Permission gate (all hooks are called above; safe to early-return now)
  if (!hasPermission('MANAGE_PROCEDURE_CATALOG')) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🧰 Procedure Catalog
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Clinic-wide procedure library used by treatment plans.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={showAdd}>
            + Add Procedure
          </button>
        </div>
        <FeesBillingTabs active="procedures" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        {/* Category pill tabs */}
        <div className="pcp-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.value}
              role="tab"
              aria-selected={selectedCategory === t.value}
              className={`pcp-tab ${selectedCategory === t.value ? 'pcp-tab-active' : ''}`}
              onClick={() => setCategory(t.value)}
            >
              {t.label}
              <span className="pcp-tab-count">{counts[t.value] ?? 0}</span>
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="pcp-filter-bar">
          <input
            type="text"
            className="pcp-search"
            placeholder="Search by name, id, specialty, ADA code…"
            value={searchQuery}
            onChange={(e) => setSearch(e.target.value)}
          />
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {/* B5 (bug 3) — MEMBERSHIP dept-filter. Hidden on a dark org. */}
          {activeDepts.length > 0 && (
            <select
              className="rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100"
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          <span className="pcp-count">
            {visible.length} of {procedures.length}
          </span>
        </div>

        {/* Add form */}
        {showAdd && (
          <div className="pcp-add-wrap">
            <div className="pcp-add-title">New Procedure</div>
            <ProcedureFormRow
              data={newRow}
              errors={newErrors}
              onChange={changeNew}
              onSave={submitNew}
              onCancel={cancelAdd}
              isEdit={false}
              isSubmitting={isSubmitting}
              departments={activeDepts}
              selectedDeptIds={newDeptIds}
              onToggleDept={toggleNewDept}
            />
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button className="pcp-btn pcp-btn-secondary" onClick={() => { clearError(); fetchProcedures(); }}>
              Retry
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="pcp-state">
            {procedures.length === 0
              ? '🗂 No procedures yet. Click + Add Procedure to create the first one.'
              : 'No procedures match your filter.'}
          </div>
        ) : (
          <table className="pcp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Clinical type</th>
                <th>Cost</th>
                <th>GST</th>
                <th>Flags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const key = `${p.category}:${p.procedure_id}`;
                const isEditing = editingKey === key;
                const activeFlags = PROCEDURE_FLAGS.filter((f) => p[f.key]);
                return (
                  <React.Fragment key={key}>
                    <tr className={p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}>
                      <td>
                        <div className="pcp-name">{p.name}</div>
                        <div className="pcp-subname">
                          {p.procedure_id}
                          {p.specialty && ` · ${p.specialty}`}
                          {p.ada_code && ` · ${p.ada_code}`}
                        </div>
                      </td>
                      <td>{PROCEDURE_CATEGORY_LABELS[p.category] || p.category}</td>
                      <td className="pcp-cost">₹{Number(p.default_cost || 0).toFixed(2)}</td>
                      <td>{p.gst_rate ?? 0}%</td>
                      <td>
                        <div className="pcp-chips">
                          {activeFlags.map((f) => <FlagChip key={f.key} flag={f} />)}
                          {p.active === false && (
                            <span className="pcp-chip pcp-chip-inactive">Deactivated</span>
                          )}
                          {/* A4/S2 — current department tags (active edges). No tag = org-wide. */}
                          {(p.department_ids || []).length > 0 && (
                            <span className="pcp-depts-caption">Visible to:</span>
                          )}
                          {(p.department_ids || []).map((did) => (
                            <span
                              key={did}
                              className="pcp-chip"
                              style={{
                                background: isDark ? '#3730a3' : '#e0e7ff',
                                color: isDark ? '#e0e7ff' : '#3730a3',
                                borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                              }}
                            >
                              🏷 {deptNameById[did] || did}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="pcp-actions">
                          <button className="pcp-btn pcp-btn-link" onClick={() => startEdit(p)} disabled={isEditing}>
                            Edit
                          </button>
                          {p.active !== false && (
                            <button
                              className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                              onClick={() => askDeactivate(p)}
                            >
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isEditing && (
                      <tr>
                        <td colSpan={6} className="pcp-edit-cell">
                          <ProcedureFormRow
                            data={editRow}
                            errors={editErrors}
                            onChange={changeEdit}
                            onSave={submitEdit}
                            onCancel={cancelEdit}
                            isEdit={true}
                            isSubmitting={isUpdating}
                            departments={activeDepts}
                            selectedDeptIds={editDeptIds}
                            onToggleDept={toggleEditDept}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </main>

      {confirmDialog && (
        <ConfirmationModal
          isOpen={true}
          title={confirmDialog.title}
          message={confirmDialog.message}
          ctaLabel={confirmDialog.ctaLabel}
          cancelLabel="Cancel"
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}
